import {useI18n} from 'i18n/I18nContext'
import React, {useCallback, useEffect, useRef, useState} from 'react'
import {BlockElements} from 'TemplateEditor/Layout/Blocks'
import {SectionConfigType} from 'TemplateEditor/Layout/Sections/SectionConfig'
import {Template} from 'TemplateEditor/template'
import {useTemplate} from 'TemplateEditor/TemplateProvider'
import {updateNestedJsonWithStringKeys} from 'utils/lib/json'
import {confirmAlert} from 'react-confirm-alert'
import {SubSectionConfigType} from 'TemplateEditor/Layout/Sections/SubSectionConfig'
import {
  TemplateEditorUnsavedChangesDialog,
  type TemplateEditorUnsavedCustomUiArgs,
} from 'TemplateEditor/TemplateEditorUnsavedChangesDialog'

export type Modes = 'section' | 'block'

export interface EditorContextProps {
  mode: Modes
  setMode: (mode: Modes) => void
  editMode: boolean
  setEditMode: (editMode: boolean) => void
  mobileMode: boolean
  setMobileMode: (mobileMode: boolean) => void
  keepSidebar: boolean
  setKeepSidebar: (keepSidebar: boolean) => void
  setEditElement: (type: string | null) => void
  editElement: string | null
  updateElement: (
    block: BlockElements | SectionConfigType | SubSectionConfigType,
  ) => void
  setElement: (
    block: BlockElements | SectionConfigType | SubSectionConfigType,
  ) => void
  /**
   * When set (e.g. attendee stream preview), blocks/sections with `visible_ticket_ids`
   * are shown only if this id is in that list. `undefined` skips tier filtering.
   */
  viewerEventTicketId: number | null | undefined
  setViewerEventTicketId: (id: number | null | undefined) => void
  /**
   * Organization template editor: preview mode must not mount attendee-only hooks
   * (e.g. sales → {@see useAttendeeBilling}).
   */
  nonInteractiveSalesInViewerMode: boolean
  /**
   * After delete/duplicate-style template mutations: close the right panel without
   * treating it as a normal “leave editor” navigation (avoids unsaved prompt).
   */
  clearSelectionAfterMutation: (nextTemplate: Template) => void
}

export const EditorContext = React.createContext<
  EditorContextProps | undefined
>(undefined)

export default function EditorProvider(props: {
  children: React.ReactElement
  editMode?: boolean
  viewerEventTicketId?: number | null
  /** When true, sales blocks stay non-interactive in preview (no attendee billing). */
  nonInteractiveSalesInViewerMode?: boolean
}) {
  const {t} = useI18n()
  const {template, updateTemplate, setTemplate, syncedTemplate} = useTemplate()

  const [mode, setMode] = useState<Modes>('section')
  const [editMode, setEditMode] = useState<boolean>(props.editMode ?? true)
  const [viewerEventTicketId, setViewerEventTicketId] = useState<
    number | null | undefined
  >(props.viewerEventTicketId)

  useEffect(() => {
    setViewerEventTicketId(props.viewerEventTicketId)
  }, [props.viewerEventTicketId])

  const nonInteractiveSalesInViewerMode =
    props.nonInteractiveSalesInViewerMode ?? false
  const [mobileMode, setMobileMode] = useState<boolean>(false)
  const [keepSidebar, setKeepSidebar] = useState<boolean>(false)
  const [editElement, changeEditElement] = useState<string | null>(null)
  const [savedTemplate, setSavedTemplate] = useState<Template | null>(null)

  useEffect(() => {
    if (!savedTemplate) return
    setSavedTemplate(JSON.parse(JSON.stringify(template)))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [syncedTemplate])

  const updateElement = (
    block: BlockElements | SectionConfigType | SubSectionConfigType,
  ) => {
    const updatedTemplate = updateNestedJsonWithStringKeys(
      template,
      editElement,
      block,
    )

    updateTemplate(updatedTemplate)
    setTemplate(updatedTemplate)
    changeEditElement(null)
  }

  const setElement = (
    block: BlockElements | SectionConfigType | SubSectionConfigType,
  ) => {
    const updatedTemplate = updateNestedJsonWithStringKeys(
      template,
      editElement,
      block,
    )

    setTemplate(updatedTemplate)
  }

  const clearSelectionAfterMutation = useCallback((nextTemplate: Template) => {
    changeEditElement(null)
    setSavedTemplate(JSON.parse(JSON.stringify(nextTemplate)))
  }, [])

  const setEditElement = (element: string | null) => {
    if (editElement !== element && savedTemplate) {
      const hasChanges =
        JSON.stringify(template) !== JSON.stringify(savedTemplate)

      if (hasChanges) {
        confirmAlert({
          title: t('templateEditor.unsavedChangesTitle'),
          message: t('templateEditor.unsavedChangesPrompt'),
          closeOnClickOutside: false,
          overlayClassName: 'template-editor-unsaved-overlay',
          customUI: (opts) => {
            const {title, message, buttons, onClose} =
              opts as TemplateEditorUnsavedCustomUiArgs
            return (
              <TemplateEditorUnsavedChangesDialog
                title={title ?? ''}
                message={message}
                buttons={buttons}
                onClose={onClose}
              />
            )
          },
          buttons: [
            {
              label: t('templateEditor.unsavedChangesStay'),
              onClick: () => {},
            },
            {
              label: t('templateEditor.unsavedChangesDiscard'),
              onClick: () => {
                setTemplate(savedTemplate)
                setSavedTemplate(JSON.parse(JSON.stringify(savedTemplate)))
                changeEditElement(element)
              },
            },
            {
              label: t('templateEditor.unsavedChangesSave'),
              onClick: () => {
                const snapshot = JSON.parse(
                  JSON.stringify(template),
                ) as Template
                updateTemplate(snapshot)
                setSavedTemplate(snapshot)
                changeEditElement(element)
              },
            },
          ],
        })
      } else {
        changeEditElement(element)
      }
    } else if (element === null && savedTemplate) {
      setTemplate(savedTemplate)
      changeEditElement(element)
    } else {
      setSavedTemplate(JSON.parse(JSON.stringify(template)))
      changeEditElement(element)
    }
  }

  const setEditElementRef = useRef(setEditElement)
  setEditElementRef.current = setEditElement

  useEffect(() => {
    if (!editElement) return

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      e.preventDefault()
      setEditElementRef.current(null)
    }

    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [editElement])

  return (
    <EditorContext.Provider
      value={{
        mode,
        setMode,
        editMode,
        setEditMode,
        mobileMode,
        setMobileMode,
        keepSidebar,
        setKeepSidebar,
        setEditElement,
        editElement,
        updateElement,
        setElement,
        viewerEventTicketId,
        setViewerEventTicketId,
        nonInteractiveSalesInViewerMode,
        clearSelectionAfterMutation,
      }}
    >
      {props.children}
    </EditorContext.Provider>
  )
}

export function useEditor() {
  const context = React.useContext(EditorContext)
  if (context === undefined) {
    throw new Error('useEditor must be used within EditorProvider')
  }
  return context
}
